// tslint:disable:no-import-side-effect
import 'isomorphic-fetch';
// tslint:disable:no-import-side-effect
import { Failure, GeneralError } from './Failure';

import type { PublicApi } from '../type-helpers';

export interface Request<Response, EncodedResponse = unknown> {
	readonly api?: 'api' | 'pigeon';
	readonly body?: BodyInit | null;
	readonly contentType?: string;
	readonly headers?: object;
	readonly isAuthenticated?: boolean;
	readonly knownErrorCodes: string[];
	readonly method?: 'get' | 'post' | 'put' | 'delete';
	readonly path: string;
	decode?(response: EncodedResponse): Response;
}

export interface Handler {
	decode<R extends Request<Response>, Response>(response: globalThis.Response, request: R): Promise<Response>;
	prepare<R extends Request<Response>, Response>(urlRequest: RequestInit, request: R): RequestInit;
}

export class Client implements IApiClient {
	private readonly baseUrl: string;
	private readonly handler: Handler;

	public constructor(baseUrl: string, handler: Handler) {
		this.baseUrl = baseUrl;
		this.handler = handler;
	}

	public async fetch<R extends Request<Response>, Response>(request: R): Promise<Response> {
		const urlRequest = this.handler.prepare(this.createUrlRequest(request), request);
		const response = await this.checkedFetch(`${this.baseUrl}${request.path}`, urlRequest);
		return this.handler.decode<R, Response>(response, request);
	}

	private async checkedFetch(url: string, urlRequest: RequestInit): Promise<globalThis.Response> {
		try {
			return await fetch(url, urlRequest);
		} catch (error) {
			throw new Failure(new Set([GeneralError.fetchFailed]));
		}
	}

	private createUrlRequest<R extends Request<Response>, Response>(request: R): RequestInit {
		return {
			body: request.body ?? null,
			headers: {
				'Content-Type': request.contentType ?? 'application/json',
			},
			method: request.method ?? 'get',
		};
	}
}

export type IApiClient = PublicApi<Client>;
